import { z } from 'zod';
import { isoDateTimeSchema, localTimeSchema, uuidSchema } from './common.js';
import { schedulePartSchema } from './playback.js';
// ---------------------------------------------------------------------------
// The venue's music on waves (client-redesign arch §5, §14, §15; ADR-050).
//
// New data ships on new reads: `GET /player/state` is a new route no bundle from before the
// release calls, and `POST /player/next-track` keeps its frozen shape (ADR-038). Until the
// switch the new tree is the only reader of this file, so a story adding its fields breaks
// nothing; after the switch these are player reads like any other. Each story's first commit
// is its part of this file (arch §15).
// ---------------------------------------------------------------------------
export const waveCodeSchema = z.enum(['groove', 'dune']);
/** A wave's name as a person reads it; the dictionary seeds the same two (arch §5.1). */
export const WAVE_NAMES = {
    groove: 'Groove',
    dune: 'Dune',
};
/**
 * The hours of a venue that has not set its schedule, whatever its type (D-22, arch §9.2):
 * `ensureVenue` and `switch` write them, and «Вернуть стандартные часы» puts them into a draft.
 */
export const STANDARD_DAY_PART_STARTS = {
    morning: '08:00',
    day: '13:00',
    evening: '18:00',
};
/** What a part plays: a wave, the venue's personal preset on Pro, or Lite's stream. */
export const playsSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('wave'), wave: waveCodeSchema }).strict(),
    z.object({ kind: z.literal('preset'), preset_id: uuidSchema, name: z.string() }).strict(),
    z.object({ kind: z.literal('stream'), name: z.string().nullable() }).strict(),
]);
/** `GET /player/state?current_track_id=` — the track the device is playing, for its chips. */
export const playerStateQuerySchema = z
    .object({
    current_track_id: uuidSchema.optional(),
})
    .strict();
/**
 * The part the venue is in (arch §9.1). The server computes it and the client prints it
 * (ADR-030): `start_local`–`end_local` is what a person reads, and `ends_at` is the instant to
 * refresh at, without arithmetic on the client.
 */
export const playerStatePartSchema = z
    .object({
    code: schedulePartSchema,
    start_local: localTimeSchema,
    end_local: localTimeSchema,
    ends_at: isoDateTimeSchema,
    crosses_midnight: z.boolean(),
})
    .strict();
/** One row of «Далее». Empty until STORY-120 fills the queue. */
export const playerStateUpNextRowSchema = z
    .object({
    track_id: uuidSchema,
    title: z.string(),
    artist: z.string().nullable(),
    tempo_label: z.string().nullable(),
    cover_url: z.string().nullable(),
})
    .strict();
/** The chips of the track in `current_track_id` (A-13); `null` when none was named. */
export const playerStateCurrentSchema = z
    .object({
    track_id: uuidSchema,
    genre_label: z.string().nullable(),
    tempo_label: z.string().nullable(),
})
    .strict();
/**
 * `GET /player/state` (`@PlayerAuth()`, `@SkipThrottle()`). `mode` picks the route tree:
 * `presets` the old one, `waves` or `stream` the new one. A venue on presets answers with
 * nothing but the mode, the version and the cadence the server asks for.
 */
export const playerStateResponseSchema = z.discriminatedUnion('mode', [
    z
        .object({
        mode: z.literal('presets'),
        version: z.number().int().nonnegative(),
        poll_after_ms: z.number().int().positive(),
    })
        .strict(),
    z
        .object({
        mode: z.enum(['waves', 'stream']),
        version: z.number().int().nonnegative(),
        poll_after_ms: z.number().int().positive(),
        part: playerStatePartSchema,
        plays: playsSchema,
        /** «В ротации: N треков»: the pool of the current part, stored at each build. */
        rotation_count: z.number().int().nonnegative(),
        up_next: z.array(playerStateUpNextRowSchema).max(5),
        current: playerStateCurrentSchema.nullable(),
    })
        .strict(),
]);
//# sourceMappingURL=waves.js.map